import config from "../config";

const HEIC_EXTENSIONS = new Set(["heic", "heif"]);
const HEIC_MIME_TYPES = new Set([
	"image/heic",
	"image/heif",
	"image/heic-sequence",
	"image/heif-sequence",
]);

export const IMAGE_FILE_EXTENSIONS = [
	"jpg",
	"jpeg",
	"png",
	"gif",
	"webp",
	"svg",
	"bmp",
	"ico",
	"heic",
	"heif",
];

export const VIDEO_FILE_EXTENSIONS = ["mp4", "webm", "mov", "avi", "mkv", "wmv", "flv", "m4v", "ogv"];

const VIDEO_MIME_TYPES = new Set([
	"video/mp4",
	"video/webm",
	"video/quicktime",
	"video/x-msvideo",
	"video/x-matroska",
	"video/ogg",
]);

export function isHeicMime(mimeType = "") {
	return HEIC_MIME_TYPES.has(String(mimeType || "").trim().toLowerCase());
}

export function getFileExtension(value = "") {
	const normalized = String(value || "").trim().toLowerCase();
	const dotIndex = normalized.lastIndexOf(".");
	if (dotIndex < 0) return "";
	return normalized.slice(dotIndex + 1);
}

export function isHeicFile({ mimeType, name, extension } = {}) {
	if (isHeicMime(mimeType)) return true;
	const ext = String(extension || getFileExtension(name) || "")
		.replace(/^\./, "")
		.toLowerCase();
	return HEIC_EXTENSIONS.has(ext);
}

export function isRasterImageFile({ mimeType, name, extension, type } = {}) {
	const mime = String(mimeType || type || "").trim().toLowerCase();
	if (mime.startsWith("image/") && mime !== "image/svg+xml") return true;
	const ext = String(extension || getFileExtension(name) || "")
		.replace(/^\./, "")
		.toLowerCase();
	return IMAGE_FILE_EXTENSIONS.includes(ext) && ext !== "svg";
}

export function isVideoFile({ mimeType, name, extension, type } = {}) {
	const mime = String(mimeType || type || "").trim().toLowerCase();
	if (mime.startsWith("video/") || VIDEO_MIME_TYPES.has(mime)) return true;
	const ext = String(extension || getFileExtension(name) || "")
		.replace(/^\./, "")
		.toLowerCase();
	return VIDEO_FILE_EXTENSIONS.includes(ext);
}

export function getPreviewMimeType(file = {}) {
	const mime = String(file.mimeType || file.type || "").trim().toLowerCase();
	if (mime) return mime;
	const ext = String(file.extension || getFileExtension(file.name || file.originalName) || "")
		.replace(/^\./, "")
		.toLowerCase();
	if (ext === "pdf") return "application/pdf";
	if (ext === "mp4" || ext === "m4v") return "video/mp4";
	if (ext === "webm") return "video/webm";
	if (ext === "mov") return "video/quicktime";
	if (ext === "avi") return "video/x-msvideo";
	if (ext === "mkv") return "video/x-matroska";
	if (ext === "ogv") return "video/ogg";
	if (IMAGE_FILE_EXTENSIONS.includes(ext)) return `image/${ext === "jpg" ? "jpeg" : ext}`;
	return "";
}

export function needsBrowserPreviewConversion(file = {}) {
	return isHeicFile({
		mimeType: file.mimeType || file.type,
		name: file.name || file.originalName,
		extension: file.extension,
	});
}

function resolveVersionedApiBase() {
	const version = config.api.API_Version || "v3";
	const explicit = String(config.api.API_URL || "").trim().replace(/\/+$/, "");
	const useDevProxy =
		process.env.NODE_ENV === "development" &&
		process.env.REACT_APP_API_USE_PROXY !== "false";

	if (useDevProxy || !explicit) return `/${version}`;
	return `${explicit}/${version}`;
}

export function getUploadBrowserPreviewUrl(upload) {
	if (!upload?._id || !needsBrowserPreviewConversion(upload)) return "";
	return `${resolveVersionedApiBase()}/system/upload/${upload._id}/browser-preview`;
}

export function getFileItemBrowserPreviewUrl(file, base = "files") {
	if (!file?._id || !needsBrowserPreviewConversion(file)) return "";
	const normalizedBase = String(base || "files").replace(/^\/+|\/+$/g, "");
	return `${resolveVersionedApiBase()}/${normalizedBase}/items/${file._id}/browser-preview`;
}
